import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { BackendInternalErrorProvider } from '../backend-internal-error/BackendInternalErrorProvider';

const QueryProvider = ({ children }: { children: ReactNode }) => {
  const [isError, setIsError] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            console.error('An error occurred:', error);

            if (
              typeof error === 'object' &&
              'response' in error &&
              error.response &&
              typeof error.response === 'object' &&
              'status' in error.response &&
              error.response.status &&
              typeof error.response.status === 'number' &&
              error.response.status >= 500 &&
              error.response.status < 600
            ) {
              setIsError(true);
            }
          },
          onSuccess: () => {
            setIsError(false);
          },
        }),
      }),
  );

  useEffect(() => {
    if (!isError) {
      return;
    }

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const hasServerError = queryClient
        .getQueryCache()
        .getAll()
        .some((query) => {
          const error = query.state.error;

          return (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'status' in error.response &&
            typeof error.response.status === 'number' &&
            error.response.status >= 500 &&
            error.response.status < 600
          );
        });

      if (!hasServerError) {
        setIsError(false);
      }
    });

    return unsubscribe;
  }, [isError, queryClient]);

  const onRefetch = useCallback(() => {
    queryClient.refetchQueries();
  }, [queryClient]);

  return (
    // Provide the client to your App
    <QueryClientProvider client={queryClient}>
      <BackendInternalErrorProvider isError={isError} onRefetch={onRefetch}>
        {children}
      </BackendInternalErrorProvider>
    </QueryClientProvider>
  );
};

export { QueryProvider };
